import { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { useIntersectionObserver } from "@/hooks/use-intersection-observer";
import { Heading } from "@/components/ui/heading";
import { SteampunkCarousel } from "@/components/ui/steampunk-carousel";
import { useIsMobile } from "@/hooks/use-mobile";
import { useFestivalRegistry } from "@/hooks/use-festival-registry";
import { useRouteRegistry } from "@/route-data";
import { formatFestivalDates } from "@/lib/date-utils";

/** Upgrade Unsplash URL to higher resolution for landing page full-bleed cards */
function retinaUrl(url: string | undefined, width = 800): string | undefined {
  if (!url) return url;
  return url.replace(/[?&]w=\d+/, `?w=${width}`);
}

function truncate(text: string, maxLen: number): string {
  if (text.length <= maxLen) return text;
  return text.slice(0, maxLen).replace(/\s+\S*$/, "") + "...";
}

/** Fallback gradients by festival category */
const CATEGORY_GRADIENTS: Record<string, string> = {
  music: "from-pink-600 to-purple-700",
  conference: "from-cyan-500 to-blue-700",
  art: "from-orange-500 to-red-600",
  cultural: "from-amber-600 to-yellow-700",
  food: "from-emerald-600 to-green-800",
  sports: "from-blue-500 to-indigo-700",
};
const DEFAULT_GRADIENT = "from-pink-600 to-purple-700";

/** Fallback gradients by route category */
const ROUTE_CATEGORY_GRADIENTS: Record<string, string> = {
  pilgrimage: "from-amber-600 to-yellow-700",
  "scenic-drive": "from-blue-500 to-indigo-700",
  coastal: "from-cyan-500 to-blue-700",
  hiking: "from-emerald-600 to-green-800",
  "cultural-route": "from-orange-500 to-red-600",
  "food-trail": "from-amber-500 to-orange-600",
  adventure: "from-emerald-600 to-green-800",
  "rail-journey": "from-indigo-500 to-purple-700",
  conference: "from-cyan-500 to-blue-700",
};
const DEFAULT_ROUTE_GRADIENT = "from-amber-600 to-yellow-700";

const AVATAR_COLORS = ["bg-purple-400", "bg-cyan-400", "bg-pink-400", "bg-amber-400", "bg-emerald-400"];

const routeKeyExtractor = (r: { data: { id: string } }) => r.data.id;

function AttendeesPill({ count, t }: { count: number; t: (key: string, opts?: Record<string, unknown>) => string }) {
  if (count <= 0) return null;
  const dotCount = Math.min(count, 3);
  return (
    <div className="inline-flex items-center gap-1.5 bg-black/30 backdrop-blur-sm rounded-full px-2.5 py-1 mt-3">
      <div className="flex -space-x-1.5">
        {Array.from({ length: dotCount }).map((_, i) => (
          <div
            key={i}
            className={`h-4 w-4 rounded-full border border-white/20 ${AVATAR_COLORS[i % 5]}`}
            style={{ filter: "blur(3px)" }}
          />
        ))}
      </div>
      <span className="text-xs font-medium text-white/90">
        {t("destinations.going", { count })}
      </span>
    </div>
  );
}

interface RouteItem {
  data: { id: string; shortName: string; location: string; description?: string; difficulty?: string; duration: string; distance: string; imageUrl?: string };
  gradient: string;
}

export function FeaturedDestinations() {
  const { t } = useTranslation("landing");
  const [ref, isVisible] = useIntersectionObserver<HTMLElement>({ threshold: 0.1 });
  const isMobile = useIsMobile();

  const { data: registryData = [], isLoading: festivalsLoading } = useFestivalRegistry();

  // Dynamically pick 3 featured routes — prefer major/mega tier, mix categories
  const { data: routeRegistryData = [] } = useRouteRegistry();

  const routes = useMemo<RouteItem[]>(() => {
    const tierOrder: Record<string, number> = { mega: 0, major: 1, mid: 2, local: 3, micro: 4 };
    const sorted = [...routeRegistryData].sort((a, b) => {
      const tierDiff = (tierOrder[a.tier] ?? 3) - (tierOrder[b.tier] ?? 3);
      if (tierDiff !== 0) return tierDiff;
      return a.name.localeCompare(b.name);
    });

    // Pick top 3, preferring diverse categories
    const picked: typeof sorted = [];
    const usedCategories = new Set<string>();
    for (const route of sorted) {
      if (picked.length >= 3) break;
      if (!usedCategories.has(route.category)) {
        picked.push(route);
        usedCategories.add(route.category);
      }
    }
    // Fill remaining slots if we didn't get 3 unique categories
    for (const route of sorted) {
      if (picked.length >= 3) break;
      if (!picked.includes(route)) picked.push(route);
    }

    return picked.map((r) => ({
      data: r,
      gradient: ROUTE_CATEGORY_GRADIENTS[r.category] ?? DEFAULT_ROUTE_GRADIENT,
    }));
  }, [routeRegistryData]);

  // Dynamically pick the 3 nearest upcoming festivals (major/mega tier preferred)
  const festivals = useMemo(() => {
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    const upcoming = registryData
      .filter((f) => {
        const lastDate = f.dates?.[f.dates.length - 1];
        return lastDate && lastDate >= today;
      })
      .sort((a, b) => {
        // Prefer major/mega tier, then sort by start date
        const tierOrder: Record<string, number> = { mega: 0, major: 1, mid: 2, local: 3, micro: 4 };
        const tierDiff = (tierOrder[a.tier] ?? 3) - (tierOrder[b.tier] ?? 3);
        if (tierDiff !== 0) return tierDiff;
        return (a.dates?.[0] ?? "").localeCompare(b.dates?.[0] ?? "");
      })
      .slice(0, 3);

    return upcoming.map((entry) => ({
      data: entry,
      gradient: CATEGORY_GRADIENTS[entry.category] ?? DEFAULT_GRADIENT,
    }));
  }, [registryData]);

  const { data: rsvpCounts } = useQuery<Record<string, number>>({
    queryKey: ["/api/festivals/rsvp-counts"],
    queryFn: async () => {
      try {
        const res = await fetch("/api/festivals/rsvp-counts", { credentials: "include" });
        if (!res.ok) return {};
        return res.json();
      } catch {
        return {};
      }
    },
    staleTime: 60_000,
  });

  const renderRouteCard = useCallback(
    (route: RouteItem) => {
      const { data, gradient } = route;
      return (
        <Link
          href={`/route/${data.id}`}
          className="group relative block rounded-xl overflow-hidden min-h-[160px] h-full"
          data-testid={`card-route-${data.id}`}
        >
          <div className={`absolute inset-0 bg-gradient-to-br ${gradient}`} />
          {data.imageUrl && (
            <img
              src={retinaUrl(data.imageUrl)}
              srcSet={`${retinaUrl(data.imageUrl, 400)} 400w, ${retinaUrl(data.imageUrl, 800)} 800w`}
              sizes="(max-width: 768px) 75vw, 33vw"
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              referrerPolicy="no-referrer"
              onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10" />
          <div className="relative z-10 flex flex-col justify-between h-full text-white p-5">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Heading level={3} as="h4" className="text-lg font-bold">
                  {data.shortName}
                </Heading>
                <span className="text-xs px-2 py-0.5 bg-white/20 rounded-full font-medium">
                  {t(`destinations.difficulty.${data.difficulty}`)}
                </span>
              </div>
              <p className="text-sm opacity-80 mb-1">{data.location}</p>
              <p className="text-xs opacity-70 mb-3">
                {data.duration} &middot; {data.distance}
              </p>
              <p className="text-sm opacity-75 leading-relaxed">
                {truncate(data.description ?? "", 100)}
              </p>
            </div>
          </div>
        </Link>
      );
    },
    [t],
  );

  return (
    <section
      ref={ref}
      id="destinations"
      className="py-20 md:py-28 scroll-mt-20"
      data-testid="section-destinations"
    >
      {/* Section heading */}
      <div className="max-w-7xl mx-auto px-4 mb-10">
        <Heading
          level={1}
          as="h2"
          serif
          className={`text-3xl md:text-4xl lg:text-5xl font-black mb-3 transition-all duration-700 ${
            isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
          }`}
        >
          {t("destinations.title")}
        </Heading>
        <p
          className={`text-base lg:text-lg text-muted-foreground max-w-xl transition-all duration-700 delay-100 ${
            isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
          }`}
        >
          {t("destinations.subtitle")}
        </p>
      </div>

      {/* Festival cards grid */}
      <div className="max-w-7xl mx-auto px-4">
        <div
          className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 transition-all duration-700 delay-200 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          {festivalsLoading
            ? Array.from({ length: 3 }).map((_, idx) => (
                <div
                  key={idx}
                  className="rounded-xl bg-muted animate-pulse min-h-[180px]"
                  data-testid={`card-festival-skeleton-${idx}`}
                />
              ))
            : festivals.map(({ data, gradient }, idx) => (
            <Link
              key={data.id}
              href={`/festival/${data.id}`}
              className={`group relative block rounded-xl overflow-hidden min-h-[180px] transition-transform duration-300 hover:scale-[1.02] ${
                idx === 0 ? "md:col-span-2 lg:col-span-1" : ""
              }`}
              style={{ backgroundColor: 'hsl(var(--primary))' }}
              data-testid={`card-festival-${idx}`}
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${gradient}`} />
              {data.imageUrl && (
                <img
                  src={retinaUrl(data.imageUrl)}
                  srcSet={`${retinaUrl(data.imageUrl, 400)} 400w, ${retinaUrl(data.imageUrl, 800)} 800w`}
                  sizes="(max-width: 768px) 100vw, 33vw"
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover"
                  referrerPolicy="no-referrer"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                />
              )}
              {/* Dark overlay for text readability */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10 group-hover:from-black/60 transition-colors duration-300" />
              {/* Card content — z-10 to sit above image */}
              <div className="relative z-10 flex flex-col justify-between h-full text-white p-6">
                <div>
                  <Heading
                    level={3}
                    as="h3"
                    className="text-xl md:text-2xl font-black mb-1"
                  >
                    {data.shortName}
                  </Heading>
                  <p className="text-sm opacity-90 font-medium mb-1">
                    {formatFestivalDates(data.dates)}
                  </p>
                  <p className="text-sm opacity-80 mb-3">{data.location}</p>
                  <p className="text-sm opacity-75 leading-relaxed">
                    {data.description ? truncate(data.description, 120) : ""}
                  </p>
                  <AttendeesPill count={rsvpCounts?.[data.id] || 0} t={t} />
                </div>
                <div className="flex items-center gap-4 mt-4 text-xs opacity-80">
                  {data.category && (
                    <span className="capitalize">{data.category}</span>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Browse All Festivals link */}
        <div className="text-center mt-8">
          <Link
            href="/festival-guide"
            className="inline-flex items-center gap-2 text-primary hover:underline font-medium"
          >
            {t("destinations.browseAll")}
            <span aria-hidden="true">&rarr;</span>
          </Link>
        </div>
      </div>

      {/* Journey/Route teaser section */}
      <div className="max-w-7xl mx-auto px-4 mt-16">
        <Heading
          level={2}
          as="h3"
          serif
          className={`text-2xl md:text-3xl font-black mb-2 transition-all duration-700 delay-300 ${
            isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
          }`}
        >
          {t("destinations.journeysTitle")}
        </Heading>
        <p
          className={`text-base text-muted-foreground mb-8 transition-all duration-700 delay-400 ${
            isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
          }`}
        >
          {t("destinations.journeysSubtitle")}
        </p>

        {/* Route cards - JukeboxCarousel on mobile, grid on desktop */}
        {isMobile ? (
          <div
            className={`transition-all duration-700 delay-500 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <SteampunkCarousel
              items={routes}
              renderSlide={renderRouteCard}
              keyExtractor={routeKeyExtractor}
              loop={false}
              showNavigation
            />
          </div>
        ) : (
          <div
            className={`grid grid-cols-3 gap-4 transition-all duration-700 delay-500 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            {routes.map(({ data, gradient }) => (
              <Link
                key={data.id}
                href={`/route/${data.id}`}
                className="group relative block rounded-xl overflow-hidden min-h-[160px] transition-transform duration-300 hover:scale-[1.02]"
                data-testid={`card-route-${data.id}`}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${gradient}`} />
                {data.imageUrl && (
                  <img
                    src={retinaUrl(data.imageUrl)}
                    srcSet={`${retinaUrl(data.imageUrl, 400)} 400w, ${retinaUrl(data.imageUrl, 800)} 800w`}
                    sizes="33vw"
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover"
                    referrerPolicy="no-referrer"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10 group-hover:from-black/60 transition-colors duration-300" />
                <div className="relative z-10 flex flex-col justify-between h-full text-white p-5">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Heading level={3} as="h4" className="text-lg md:text-xl font-bold">
                        {data.shortName}
                      </Heading>
                      <span className="text-xs px-2 py-0.5 bg-white/20 rounded-full font-medium">
                        {t(`destinations.difficulty.${data.difficulty}`)}
                      </span>
                    </div>
                    <p className="text-sm opacity-80 mb-1">{data.location}</p>
                    <p className="text-xs opacity-70 mb-3">
                      {data.duration} &middot; {data.distance}
                    </p>
                    <p className="text-sm opacity-75 leading-relaxed">
                      {truncate(data.description ?? "", 100)}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Explore All Routes link */}
        <div className="text-center mt-8">
          <Link
            href="/endeavor"
            className="inline-flex items-center gap-2 text-primary hover:underline font-medium"
          >
            {t("destinations.exploreRoutes")}
            <span aria-hidden="true">&rarr;</span>
          </Link>
        </div>
      </div>

    </section>
  );
}
